import React from 'react';
import ReactDOM from 'react-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { CustomerFormData, ValidationErrors } from '../types';

interface PreferencesProps {
    formData: CustomerFormData;
    errors: ValidationErrors;
    onInputChange: (field: string, value: string) => void;
}

const Preferences: React.FC<PreferencesProps> = ({
    formData,
    errors,
    onInputChange
}) => {
    const { t } = useTranslation();
    const [isPaymentTermsDropdownOpen, setIsPaymentTermsDropdownOpen] = React.useState(false);
    const [dropdownPosition, setDropdownPosition] = React.useState({ top: 0, left: 0, width: 0 });
    const paymentTermsDropdownRef = React.useRef<HTMLDivElement>(null);

    const paymentTermsOptions = [
        { value: '10', label: `10 ${t('DAYS')}` },
        { value: '15', label: `15 ${t('DAYS')}` },
        { value: '20', label: `20 ${t('DAYS')}` },
        { value: '30', label: `30 ${t('DAYS')}` },
        { value: '45', label: `45 ${t('DAYS')}` },
        { value: '60', label: `60 ${t('DAYS')}` },
    ];

    // Update dropdown position when opening
    const handleToggleDropdown = () => {
        if (!isPaymentTermsDropdownOpen && paymentTermsDropdownRef.current) {
            const rect = paymentTermsDropdownRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: rect.width
            });
        }
        setIsPaymentTermsDropdownOpen(!isPaymentTermsDropdownOpen);
    };

    // Close dropdown when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            const isClickOnDropdown = paymentTermsDropdownRef.current?.contains(target);
            const isClickOnPortal = (event.target as HTMLElement).closest('.dropdown-list');
            
            if (!isClickOnDropdown && !isClickOnPortal) {
                setIsPaymentTermsDropdownOpen(false);
            }
        };
        
        if (isPaymentTermsDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isPaymentTermsDropdownOpen]);

    const getPaymentTermLabel = (value: string) => {
        const option = paymentTermsOptions.find(opt => opt.value === value);
        return option ? option.label : '';
    };

    return (
        <>
            <div className="preferences-description">
                {t('PREFERENCES_DESCRIPTION')}
            </div>

            <Row className="mb-4">
                <Col md={12}>
                    <Form.Group className="input-field">
                        <Form.Label>
                            {t('PAYMENT_TERMS')}
                            <Form.Text className="text-muted d-inline ms-1"> {t('PAYMENT_TERMS_INFO')}
                            </Form.Text>
                        </Form.Label>
                        <div className="custom-dropdown" ref={paymentTermsDropdownRef} style={{ position: 'relative' }}>
                            <div
                                className={`dropdown-trigger ${isPaymentTermsDropdownOpen ? 'open' : ''}`}
                                onClick={handleToggleDropdown}
                                style={{
                                    position: 'relative',
                                    height: '46px',
                                    border: `1px solid ${errors.paymentTerms ? '#dc3545' : (isPaymentTermsDropdownOpen ? '#7c3aed' : '#eaebf0')}`,
                                    borderRadius: '6px',
                                    backgroundColor: '#ffffff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: isPaymentTermsDropdownOpen ? '0 0 0 0.2rem rgba(168, 85, 247, 0.18)' : 'none'
                                }}
                            >
                                <span style={{
                                    flex: 1,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: '15px',
                                    fontWeight: 500,
                                    lineHeight: '22px',
                                    color: formData.paymentTerms ? '#252525' : '#68727d'
                                }}>
                                    {formData.paymentTerms ? getPaymentTermLabel(formData.paymentTerms) : t('PAYMENT_TERM')}
                                </span>
                                <svg
                                    className={`dropdown-arrow ${isPaymentTermsDropdownOpen ? 'rotated' : ''}`}
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    style={{
                                        flexShrink: 0,
                                        color: '#68727d',
                                        transition: 'transform 0.2s ease',
                                        transform: isPaymentTermsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}
                                >
                                    <path
                                        d="M5 7.5L10 12.5L15 7.5"
                                        stroke="currentColor"
                                        strokeWidth="1.67"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>

                            {isPaymentTermsDropdownOpen && ReactDOM.createPortal(
                                <div 
                                    className="dropdown-list"
                                    style={{
                                        position: 'fixed',
                                        top: `${dropdownPosition.top}px`,
                                        left: `${dropdownPosition.left}px`,
                                        width: `${dropdownPosition.width}px`,
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #eaebf0',
                                        borderRadius: '6px',
                                        padding: '4px',
                                        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                                        zIndex: 9999
                                    }}
                                >
                                    <div className="dropdown-content">
                                        {paymentTermsOptions.map((option) => (
                                            <div
                                                key={option.value}
                                                className={`list-item ${formData.paymentTerms === option.value ? 'selected' : ''}`}
                                                onClick={() => {
                                                    onInputChange('paymentTerms', option.value);
                                                    setIsPaymentTermsDropdownOpen(false);
                                                }}
                                                style={{
                                                    padding: '10px 12px',
                                                    cursor: 'pointer',
                                                    borderRadius: '4px',
                                                    transition: 'background-color 0.2s ease',
                                                    backgroundColor: formData.paymentTerms === option.value ? '#f4efff' : 'transparent'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f4efff'}
                                                onMouseLeave={(e) => {
                                                    if (formData.paymentTerms !== option.value) {
                                                        e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                }}
                                            >
                                                <div className="text-input">
                                                    <div className="paragraph-container">
                                                        <p className="option-text" style={{ margin: 0, fontSize: '15px', color: '#252525' }}>
                                                            {option.label}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>,
                                document.body
                            )}
                        </div>
                        {errors.paymentTerms && (
                            <Form.Text className="text-danger">{t(errors.paymentTerms)}</Form.Text>
                        )}
                    </Form.Group>
                </Col>
            </Row>
        </>
    );
};

export default Preferences;